import React, { useEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Docmnt, ModelClass } from '/imports/types';
import { strings } from '/imports/strings';
import { MethodDeclaration } from '/imports/methods';
import { Form } from '/imports/ui/Form';
import { FieldSpec, FormBuilder } from '/imports/ui/FormBuilder';
import { OverlayAbsolute } from '/imports/ui/OverlayAbsolute';
import { Button } from '/imports/ui/Button';
import { css } from '/imports/ui/css';
import { Loading } from '/imports/ui/Loading';
import { deleteSavedFormForKey } from '/imports/api/savedForms';

const AutoFormStyled = styled.div`
    .buttons {
      display: flex;
      gap: 1rem;
      position: sticky;
      bottom: 0;
      padding-bottom: 1rem;
      background: ${css.white};
      width: 100%;
      .btn {
        &.save {
          flex: 1;
        }
        &.delete {
          background: ${css.red1};
          border-color: ${css.red1};
          color: ${css.white};
          &:hover {
            background: ${css.red2};
            border-color: ${css.red2};
          }
        }
      }
    }
    .btn.save {
      display: flex;
      position: sticky;
      bottom: 1rem;
      width: 100%;
    }
    h2 {
      margin: 0;
      margin-bottom: -1rem;
    }
`;

export interface AutoFormProps<T extends Docmnt = any> {
  modelClass?: ModelClass<T>;
  createMethod?: MethodDeclaration<Partial<T>, string>;
  updateMethod?: MethodDeclaration<Partial<T>, string>;
  deleteMethod?: MethodDeclaration<string, void>;
  form: FormSpec<T> | (() => FormSpec<T>);
  document?: T;
  update: Partial<T>;
  setUpdate: (value: React.SetStateAction<Partial<T>>) => void;
  header?: React.ReactNode | ((setUpdate: (value: React.SetStateAction<Partial<T>>) => void, update: Partial<T>, error: any) => React.ReactNode);
  footer?: React.ReactNode | ((setUpdate: (value: React.SetStateAction<Partial<T>>) => void, update: Partial<T>, error: any) => React.ReactNode);
  redirect?: string | null;
  submitButtonText?: string;
  save?: (update: Partial<T>, document?: T) => void | Promise<void>;
  beforeSave?: (update: Partial<T>, document?: T) => boolean | Promise<boolean>;
  onSave?: (_id: string) => void | Promise<void>;
  onError?: (error: any) => void | Promise<void>;
  cacheKey?: string;
  extraButtons?: React.ReactNode;
  submitInput?: boolean;
}

/** Form that creates/updates/deletes a document via model-class or explicit methods. */
export const AutoForm = <T extends Docmnt>
  (props: AutoFormProps<T>) => {
  const { form, document, update, setUpdate, modelClass, createMethod, updateMethod, deleteMethod, redirect, header, footer,
    submitButtonText, onSave, onError, beforeSave, cacheKey, extraButtons, submitInput } = props;
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<any>();
  const _id = update?._id || document?._id;
  const isNew = _id === 'new';
  /** Validate and persist the form, then redirect/notify per props. */
  const save = async () => {
    try {
      if (props.save) return await props.save(update, document);
      if (beforeSave) {
        const shouldContinue = await beforeSave(update, document);
        if (!shouldContinue) return;
      }
      const method = isNew ? createMethod || modelClass?.createMethod : updateMethod || modelClass?.updateMethod;
      if (!method) return console.warn('No method is configured for the save button of this form');
      setSubmitting(true);
      const _id = await method.call(update);
      setError(undefined);
      toast.success(strings.Saved());
      redirect && navigate(redirect);
      onSave?.(_id);
      cacheKey && deleteSavedFormForKey.call(cacheKey);
    }
    catch (error: any) {
      console.log(error);
      if (error?.reason === 'validationError') setError(JSON.parse(error.details));
      else if (onError) onError(error);
      else toast.error(`${error}`);
    }
    finally {
      setSubmitting(false);
    }
  };
  /** Delete the document and navigate away. */
  const deleteDocument = async () => {
    try {
      _id && await (deleteMethod || modelClass?.deleteMethod)?.call(_id);
      redirect ? navigate(redirect) : navigate(-1);
    }
    catch (error: any) {
      console.log(error);
      if (onError) onError(error);
      else toast.error(`${error}`);
    }
  };
  return (
    <AutoFormStyled>
      <Form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); save(); return false; }} submitInput={submitInput}>
        {typeof header === 'function' ? header(setUpdate, update, error) : header}
        <FormBuilder
          document={document}
          update={update}
          setUpdate={setUpdate}
          error={error}
          form={typeof form === 'function' ? form() : form}
          cacheKey={cacheKey}
        />
        {typeof footer === 'function' ? footer(setUpdate, update, error) : footer}
        <div className="buttons">
          {submitInput !== false && <Button type="submit" className="btn primary save">{submitting ? <Loading /> : submitButtonText || strings.Save()}</Button> || null}
          {extraButtons || null}
          {_id && !isNew && deleteMethod && <Button className="btn secondary delete" onClick={() => deleteDocument()}>{document?.active ? strings.Deactivate() : strings.Delete()}</Button>}
        </div>
        {submitting && <OverlayAbsolute />}
      </Form>
    </AutoFormStyled>
  );
};

export type FormSpec<T extends Docmnt> = (FieldSpec<T> | React.ReactNode)[][];

/**
 * Hook-style helper that wires up AutoForm state and returns the element plus its update setter/value.
 * @param props - AutoForm props plus optional default/controlled update state
 * @returns tuple of [component, setUpdate, update]
 */
export const autoForm = <T extends Docmnt>(props: Omit<AutoFormProps<T>, 'update' | 'setUpdate'> & {
  defaultUpdate?: Partial<T>,
  update?: Partial<T>,
  setUpdate?: (value: React.SetStateAction<Partial<T>>) => void
}) => {
  const { document, defaultUpdate, update, setUpdate } = props;
  const _id = document?._id || 'new';
  const isNew = _id === 'new';
  const _defaultUpdate = (!isNew ? { _id } : defaultUpdate || { _id, active: true }) as Partial<T>;
  const [_update, _setUpdate] = update && setUpdate ? [update, setUpdate] : useState<Partial<T>>(_defaultUpdate);
  useEffect(() => _setUpdate(_defaultUpdate as Partial<T>), [document]);
  const component = (
    <AutoForm
      {...props}
      update={_update}
      setUpdate={_setUpdate}
    />
  );
  return [component, _setUpdate, _update] as [React.JSX.Element, (value: React.SetStateAction<Partial<T>>) => void, Partial<T>];
};

/**
 * Memoize a form spec against the given dependencies.
 * @param config - form field layout
 * @param deps - memo dependency list
 * @returns the memoized form spec
 */
export const formConfig = <T extends Docmnt>(config: FormSpec<T>, deps: any[] = []) => {
  return useMemo<FormSpec<T>>(() => config, deps);
};